import React, { useEffect, useMemo, useState } from 'react';
import { ReviewActions } from '../actions';
import styles from '../view.module.css';

const SECTIONS = [
  { key: 'hook', label: 'Hook', rows: 4 },
  { key: 'transition', label: 'Transition', rows: 3 },
  { key: 'tease', label: 'Tease', rows: 4 },
  { key: 'secondary', label: 'Secondary', rows: 4 },
  { key: 'cta', label: 'CTA', rows: 3 }
];

const buildDraft = (output = {}) => {
  const { sections = {} } = output;
  return {
    subject_line: output.subject_line || '',
    preview_text: output.preview_text || '',
    hook: sections.hook || '',
    transition: sections.transition || '',
    tease: sections.tease || '',
    secondary: sections.secondary || '',
    cta: sections.cta || ''
  };
};

export const EmailJobReview = ({
  content_job = {},
  output = {},
  read_only = false,
  acting = null,
  onApprove = () => {},
  onSaveDraft = () => {},
  onReject = () => {}
}) => {
  const [draft, setDraft] = useState(() => buildDraft(output));

  useEffect(() => {
    setDraft(buildDraft(output));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content_job.id, content_job.current_version, content_job.edited_output]);

  const pristine_draft = useMemo(() => buildDraft(output), [output]);
  const has_changes = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(pristine_draft),
    [draft, pristine_draft]
  );

  const subject_line_alternatives = output.subject_line_alternatives || [];

  const buildOutput = () => ({
    ...output,
    subject_line: draft.subject_line,
    preview_text: draft.preview_text,
    sections: {
      ...(output.sections || {}),
      hook: draft.hook,
      transition: draft.transition,
      tease: draft.tease,
      secondary: draft.secondary,
      cta: draft.cta
    }
  });

  const handleFieldChange = (key, value) => {
    setDraft((previous_draft) => ({ ...previous_draft, [key]: value }));
  };

  const handleSubjectSwap = (alternative) => {
    if (read_only) return;
    handleFieldChange('subject_line', alternative);
  };

  return (
    <>
      <section className={styles.card}>
        <label className={styles.fieldLabel} htmlFor="content-job-subject-line">
          Subject line
        </label>
        <input
          id="content-job-subject-line"
          className={styles.textInput}
          value={draft.subject_line}
          onChange={(e) => handleFieldChange('subject_line', e.target.value)}
          disabled={read_only}
          placeholder="Subject line"
        />
        {subject_line_alternatives.length > 0 && (
          <div className={styles.altRow}>
            <span className={styles.altLabel}>Alternatives</span>
            {subject_line_alternatives.map((alternative, index) => (
              <button
                key={index}
                type="button"
                className={`${styles.altChip} ${
                  draft.subject_line === alternative ? styles.altChipActive : ''
                }`}
                onClick={() => handleSubjectSwap(alternative)}
                disabled={read_only}
              >
                {alternative}
              </button>
            ))}
          </div>
        )}

        <label className={styles.fieldLabel} htmlFor="content-job-preview-text">
          Preview text
        </label>
        <input
          id="content-job-preview-text"
          className={styles.textInput}
          value={draft.preview_text}
          onChange={(e) => handleFieldChange('preview_text', e.target.value)}
          disabled={read_only}
          placeholder="Preview text"
        />
      </section>

      <div className={styles.splitPane}>
        <div className={styles.splitColumn}>
          {SECTIONS.map(({ key, label, rows }) => (
            <section key={key} className={styles.card}>
              <label className={styles.fieldLabel} htmlFor={`content-job-section-${key}`}>
                {label}
              </label>
              <textarea
                id={`content-job-section-${key}`}
                className={styles.textArea}
                value={draft[key]}
                onChange={(e) => handleFieldChange(key, e.target.value)}
                disabled={read_only}
                placeholder={`${label} copy`}
                rows={rows}
              />
            </section>
          ))}
        </div>

        <div className={styles.splitColumn}>
          <section className={`${styles.card} ${styles.previewCard}`}>
            <p className={styles.previewLabel}>Live preview</p>
            <div className={styles.emailPreview}>
              <p className={styles.emailSubject}>{draft.subject_line || 'Subject line'}</p>
              <p className={styles.emailPreheader}>{draft.preview_text || 'Preview text'}</p>
              <div className={styles.emailBody}>
                {SECTIONS.map(({ key, label }) =>
                  draft[key] ? (
                    <p key={key} className={styles.emailParagraph}>
                      {draft[key]}
                    </p>
                  ) : (
                    <p key={key} className={styles.emailPlaceholder}>
                      {label}
                    </p>
                  )
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {!read_only && (
        <ReviewActions
          can_save_draft={has_changes}
          acting={acting}
          onApprove={() => onApprove(has_changes ? buildOutput() : null)}
          onSaveDraft={() => onSaveDraft(buildOutput())}
          onReject={onReject}
        />
      )}
    </>
  );
};
